<template>
  <div class="viewer-root">
    <div class="canvas-wrap" :class="{ 'is-loading': loading, 'is-error': !!error }">
      <canvas ref="canvas" class="viewer-canvas" :aria-hidden="loading || !!error"></canvas>

      <div v-if="loading" class="overlay">
        <div class="spinner" aria-hidden="true"></div>
        <p>Cargando modelo…</p>
      </div>

      <div v-if="error" class="overlay overlay-error" role="alert">
        <strong>No se pudo renderizar el modelo</strong>
        <p>{{ error }}</p>
      </div>

      <div v-if="unsupported" class="overlay overlay-warn" role="status">
        <strong>Visor 3D no disponible</strong>
        <p>
          Tu navegador no soporta WebGL. Puedes continuar con la cotización
          ingresando manualmente las dimensiones.
        </p>
      </div>

      <button
        v-if="ready"
        type="button"
        class="reset-btn"
        @click="onReset"
        title="Restablecer vista"
        aria-label="Restablecer vista"
      >
        ↻ Reset
      </button>
    </div>

    <div v-if="ready && metrics" class="metrics-bar">
      <span>
        <strong>{{ metrics.dimensionsMm.x }} × {{ metrics.dimensionsMm.y }} × {{ metrics.dimensionsMm.z }}</strong> mm
      </span>
      <span>
        <strong>{{ formattedVolume }}</strong> cm³{{ metrics.approximate ? '*' : '' }}
      </span>
      <span>
        <strong>{{ formattedTriangles }}</strong> △
      </span>
    </div>

    <p v-if="metrics && metrics.warning === 'high_density'" class="warning-text" role="status">
      Modelo de alta densidad ({{ formattedTriangles }} triángulos). El visor
      puede ralentizarse en equipos modestos.
    </p>
  </div>
</template>

<script>
export default {
  name: 'ModelViewer',
  props: {
    file: { type: File, default: null },
  },
  emits: ['model-loaded', 'model-error', 'unsupported'],
  data() {
    return {
      loading: false,
      ready: false,
      error: '',
      unsupported: false,
      metrics: null,
      controller: null,
    };
  },
  computed: {
    formattedVolume() {
      if (!this.metrics) return '';
      return this.metrics.volumeCm3.toFixed(2);
    },
    formattedTriangles() {
      if (!this.metrics) return '';
      const t = this.metrics.triangleCount;
      if (t >= 1_000_000) return (t / 1_000_000).toFixed(2) + ' M';
      if (t >= 1_000) return (t / 1_000).toFixed(1) + ' K';
      return String(t);
    },
  },
  watch: {
    file(newFile) {
      if (newFile) this.loadFile(newFile);
      else this.teardown();
    },
  },
  mounted() {
    if (this.file) this.loadFile(this.file);
  },
  beforeUnmount() {
    this.teardown();
  },
  methods: {
    async loadFile(file) {
      this.teardown();
      this.error = '';
      this.unsupported = false;
      this.loading = true;
      this.ready = false;

      try {
        const { isWebGLAvailable, mountViewer } = await import(
          /* webpackChunkName: "print3d-viewer" */ '@/services/threeLoaderRegistry'
        );

        if (!isWebGLAvailable()) {
          this.unsupported = true;
          this.loading = false;
          this.$emit('unsupported');
          return;
        }

        await this.$nextTick();
        const canvas = this.$refs.canvas;
        if (!canvas) throw new Error('Canvas no disponible.');

        this.controller = await mountViewer(file, canvas);
        this.metrics = this.controller.metrics;
        this.ready = true;
        this.loading = false;
        this.$emit('model-loaded', this.controller.metrics);
      } catch (e) {
        this.loading = false;
        this.error = e?.message || 'Error desconocido al cargar el modelo.';
        this.$emit('model-error', this.error);
      }
    },
    onReset() {
      if (this.controller) this.controller.resetView();
    },
    teardown() {
      if (this.controller) {
        this.controller.dispose();
        this.controller = null;
      }
      this.ready = false;
      this.metrics = null;
    },
  },
};
</script>

<style scoped>
.viewer-root {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.canvas-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  max-height: 480px;
  border: 1px solid #9DB2BF;
  border-radius: 12px;
  background: #F8FAFC;
  overflow: hidden;
}

.viewer-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: rgba(248, 250, 252, 0.92);
  color: #2C3E50;
  padding: 1rem;
  text-align: center;
}

.overlay-error {
  background: rgba(254, 226, 226, 0.94);
  color: #7F1D1D;
}

.overlay-warn {
  background: rgba(254, 243, 199, 0.94);
  color: #78350F;
}

.spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #9DB2BF;
  border-top-color: #526D82;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.reset-btn {
  position: absolute;
  bottom: 0.6rem;
  right: 0.6rem;
  background: white;
  border: 1px solid #9DB2BF;
  border-radius: 6px;
  padding: 0.3rem 0.7rem;
  font-size: 0.85rem;
  color: #526D82;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(11, 19, 42, 0.15);
}

.reset-btn:hover {
  background: #526D82;
  color: white;
}

.metrics-bar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.6rem 0.8rem;
  background: white;
  border: 1px solid #9DB2BF;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #4F5665;
  flex-wrap: wrap;
}

.metrics-bar strong {
  color: #2C3E50;
}

.warning-text {
  margin: 0;
  font-size: 0.8rem;
  color: #78350F;
  background: rgba(254, 243, 199, 0.6);
  padding: 0.4rem 0.7rem;
  border-radius: 6px;
}
</style>
